import { useParams } from 'react-router';

import {
  Box,
  Table,
  Skeleton,
  TableBody,
  Typography,
  TableContainer,
} from '@mui/material';

import { useTransactionHistoryByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { TableNoData, TableHeadCustom } from 'src/components/table';

import { TABLE_HEAD } from './transaction-history.constants';
import { TransactionHistoryRow } from './transaction-history-row';
import { getTransactionHistoryRowKey } from './transaction-history.utils';

export function TransactionHistoryTable() {
  const { comboId, coverageId, policyId } = useParams<{
    comboId: string;
    coverageId: string;
    policyId: string;
  }>();

  const { data, isLoading, isError } = useTransactionHistoryByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  if (isLoading) {
    return (
      <Box data-testid="transaction-history-loading">
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" data-testid="transaction-history-error" sx={{ mt: 2 }}>
        Unable to load transaction history. Please try again later.
      </Typography>
    );
  }

  const notFound = Array.isArray(data?.data) && data.data.length === 0;

  return (
    <Box sx={{ '& .MuiTableContainer-root': { paddingBottom: '24px' } }}>
      <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="transaction-history-title">
        Transaction history
      </Typography>
      <TableContainer
        sx={{
          mt: 1.5,
          border: '1px solid var(--palette-divider)',
          borderRadius: 2,
          borderBottom: '1px dashed var(--palette-divider)',
        }}
      >
        <Table sx={{ minWidth: 600 }} data-testid="transaction-history-table">
          <TableHeadCustom
            headCells={TABLE_HEAD}
            rowCount={data?.data?.length}
            data-testid="transaction-history-table-head"
          />
          <TableBody data-testid="transaction-history-table-body">
            {data?.data?.map((row, index) => (
              <TransactionHistoryRow key={getTransactionHistoryRowKey(row, index)} row={row} index={index} />
            ))}
            <TableNoData notFound={notFound} description="No transaction history found for this policy." />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
